<script setup>
import {computed} from "vue";
import {useAuthService} from "~/composables/useAuthService";
import {useTimelineModels} from "~/composables/useTimelineModels";
import AvatarImageLoader from "@/components/loaders/AvatarImageLoader.vue";
import SquashLevelsChip from "@/components/squashLevels/SquashLevelsChip.vue";
import {useHandicapSettingsFor} from "~/composables/useHandicapSettingsFor";
import {useHandicaps} from "~/composables/useHandicaps";

// Shared read-only standings table (Flutter StandingsView): columns TEAM · P
// (played) · BON (bonus) · PTS. The current user's row — and any highlightTeamIds
// (e.g. a fixture's two teams) — get a full-width brand tint; ranks below the top
// 3 dim. itemLimit caps rows (top-N on the feed; all on a page).
// Used by the Activity feed (leagueCycleDivision item) and the fixture detail page.
const props = defineProps({
  division: {type: Object, required: true},
  itemLimit: {type: Number, default: 6},
  // 1 = singles — the header column reads Player instead of Team.
  playersPerTeam: {type: Number, default: 1},
  // the league's scoring system — lets the handicap calculator show tie-break starts
  scoringSystemId: {type: String, default: null},
  showBonus: {type: Boolean, default: true},
  // Extra team ids to brand-tint (e.g. both sides of a fixture). The current
  // user's own row is always highlighted too. Mirrors Flutter StandingsView.
  highlightTeamIds: {type: Array, default: () => []},
  // false = only highlightTeamIds glow (the fixture page: the two teams playing, not the viewer)
  highlightMe: {type: Boolean, default: true},
  // Squash ratings are sport-gated, and this table is shared by league cycles,
  // tournament group stages and the activity feed — only the caller knows the
  // sport. Defaults to hidden; callers pass sportUsesSquashLevels(sport).
  // Mirrors the Flutter StandingsView prop of the same name.
  showSquashLevels: {type: Boolean, default: false},
});

const {user} = useAuthService();
const {teamInitials} = useTimelineModels();

// Team handicaps: the division carries its leagueId; settings (null = off) are cached per league.
const {settings: handicapSettings} = useHandicapSettingsFor(
    computed(() => props.division?.leagueId ? {leagueId: props.division.leagueId} : null),
    computed(() => props.division?.handicapsEnabled !== false));
const {labelFor: handicapLabelFor, chipClassFor} = useHandicaps();

// A team's rating is its player's. Singles is the ordinary case: the player's
// chip, as the API sent it (the level for someone who shares, the greyed chip
// for someone who does not, SquashLevelsChip). A doubles pairing shows nothing
// rather than an invented combined figure: an average of two ratings is not a
// squash level.
const singlePlayer = (team) => {
  const players = team?.players || [];
  return players.length === 1 ? players[0] : null;
};

const sortedTeams = computed(() => {
  const teams = [...(props.division?.teams || [])];
  teams.sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
  return teams;
});

const handicapEntries = computed(() => (props.division?.teams || []).map((t) => ({teamId: t.id, name: t.name, value: t.handicap ?? null})));
const rows = computed(() => sortedTeams.value.slice(0, props.itemLimit).map((team, i) => {
  const isYou = (team.players || []).some((p) => p.userId === user.value?.id);
  return {
    team,
    position: team.position ?? (i + 1),
    played: (team.fixtures || []).filter((f) => f.score != null).length,
    isYou,
    highlighted: (props.highlightMe && isYou) || props.highlightTeamIds.includes(team.id),
  };
}));
</script>

<template>
  <div class="tl-standings">
    <div class="ts-head">
      <div class="ts-pos"></div>
      <div class="ts-name">{{ playersPerTeam <= 1 ? 'Player' : 'Team' }}</div>
      <div class="ts-num">P</div>
      <div v-if="showBonus" class="ts-num">Bon</div>
      <div class="ts-pts">Pts</div>
    </div>
    <div
        v-for="row in rows"
        :key="row.team.id"
        class="ts-row"
        :class="{ 'ts-row--me': row.highlighted }"
    >
      <div class="ts-pos" :class="{ 'ts-pos--low': row.position > 3 }">{{ row.position }}</div>
      <div class="ts-name">
        <avatar-image-loader
            v-if="row.team.imageFileName"
            :image-file-name="row.team.imageFileName" :image-clickable="false" size="24" class="ts-face"
        />
        <span v-else class="ts-face ts-face--initials">{{ teamInitials(row.team) }}</span>
        <!-- The name, then its chips; on a narrow table the chips drop under the name -->
        <span class="ts-who">
          <span class="ts-nm">{{ row.team.name }}</span>
          <span class="ts-chips">
            <span v-if="handicapLabelFor(handicapSettings, row.team.handicap)" class="tl-chip ts-hcp" :class="chipClassFor(handicapSettings, row.team.handicap)">
              {{ handicapLabelFor(handicapSettings, row.team.handicap) }}
            </span>
            <squash-levels-chip class="ts-level" :person="singlePlayer(row.team)" :allowed="showSquashLevels"/>
          </span>
        </span>
      </div>
      <div class="ts-num">{{ row.played }}</div>
      <div v-if="showBonus" class="ts-num">{{ row.team.bonusPoints || '—' }}</div>
      <div class="ts-pts">{{ row.team.totalPoints ?? 0 }}</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tl-standings {
  container-type: inline-size;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  overflow: hidden;
}

.ts-head {
  display: flex;
  align-items: center;
  padding: 10px 14px 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.ts-row {
  display: flex;
  align-items: center;
  padding: 10px 14px;

  & + .ts-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  &--me {
    background: rgb(var(--v-theme-brandTint));

    .ts-pts {
      color: rgb(var(--v-theme-primary));
    }
  }
}

.ts-pos {
  width: 26px;
  flex-shrink: 0;
  font-size: 12.5px;
  font-weight: 700;

  &--low {
    color: rgb(var(--v-theme-textSubtle));
  }
}

.ts-name {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
}

.ts-face {
  width: 24px;
  height: 24px;
  border-radius: 50% !important;
  flex-shrink: 0;
}

.ts-face--initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  font-size: 9px;
  font-weight: 700;
}

.ts-who {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
}

.ts-chips {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* A narrow table (a phone, a side column): the chips sit on the line under the
   name, so the name keeps the width (mockup, 3 Nov 2026). */
@container (max-width: 519px) {
  .ts-who {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

.ts-nm {
  max-width: 100%;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ts-num {
  width: 38px;
  flex-shrink: 0;
  text-align: center;
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
}

.ts-pts {
  width: 40px;
  flex-shrink: 0;
  text-align: right;
  font-size: 13.5px;
  font-weight: 800;
}
.ts-hcp {
  font-size: 10px;
  font-weight: 700;
  padding: 0 6px;
  border-radius: 999px;
  margin-left: 6px;
  white-space: nowrap;
  flex-shrink: 0;
}
.ts-tools { display: flex; justify-content: flex-end; padding: 6px 10px 0; }
</style>
